import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { theme } from '../styles/theme';

type Msg = { role: 'user' | 'assistant'; content: string };

const STORAGE_KEY = 'nuled.asesor_chat.v1';
const WELCOME: Msg = {
  role: 'assistant',
  content: '¡Hola! Soy el Asesor Inteligente de NULED. Te ayudo a encontrar la iluminación LED y los controles Lutron que necesitas. Cuéntame de tu proyecto — ¿residencial o comercial?, ¿qué espacio quieres iluminar?',
};

export function AsesorAIChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? (JSON.parse(raw) as Msg[]) : [];
      return parsed.length ? parsed : [WELCOME];
    } catch { return [WELCOME]; }
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-30))); } catch {}
    // scroll al final
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const newMessages = [...messages, { role: 'user' as const, content: text }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    setErr(null);

    try {
      const { data, error } = await supabase.functions.invoke('claude-chat', {
        body: { messages: newMessages },
      });
      if (error) throw error;
      const reply = (data as { text?: string; error?: string }) ?? {};
      if (reply.error) throw new Error(reply.error);
      setMessages([...newMessages, { role: 'assistant', content: reply.text ?? '...' }]);
    } catch (e: any) {
      setErr(e.message ?? 'Error al contactar al asesor');
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([WELCOME]);
    setErr(null);
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Abrir Asesor IA"
          style={{
            position: 'fixed',
            left: 24,
            bottom: 24,
            zIndex: 99,
            background: theme.accent,
            color: theme.brand,
            border: 'none',
            borderRadius: 999,
            width: 60,
            height: 60,
            cursor: 'pointer',
            boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
            fontSize: 24,
            fontFamily: theme.font.sans,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          💡
        </button>
      )}

      {open && (
        <aside
          style={{
            position: 'fixed',
            left: 24,
            bottom: 24,
            width: 'min(380px, calc(100vw - 48px))',
            height: 'min(620px, calc(100vh - 48px))',
            zIndex: 100,
            background: theme.bg,
            border: `1px solid ${theme.border}`,
            boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: theme.font.sans,
          }}
        >
          {/* Header */}
          <div style={{
            background: theme.brand,
            color: theme.textOnBrand,
            padding: '14px 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: theme.accent, fontWeight: 700 }}>
                Asesor inteligente
              </div>
              <div style={{ fontFamily: theme.font.head, fontSize: 17, fontWeight: 600, marginTop: 1 }}>
                NULED · Iluminación + Control
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Cerrar"
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.textOnBrand,
                cursor: 'pointer',
                padding: 4,
                fontSize: 20,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {messages.map((m, i) => (
              <div
                key={i}
                style={{
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  background: m.role === 'user' ? theme.brand : theme.bgSoft,
                  color: m.role === 'user' ? theme.textOnBrand : theme.brand,
                  padding: '10px 14px',
                  fontSize: 13.5,
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {m.content}
              </div>
            ))}
            {loading && (
              <div style={{
                alignSelf: 'flex-start',
                background: theme.bgSoft,
                color: theme.textMuted,
                padding: '10px 14px',
                fontSize: 13,
                fontStyle: 'italic',
              }}>
                Pensando…
              </div>
            )}
            {err && (
              <div style={{
                background: '#FEE2E2',
                color: '#991B1B',
                padding: 10,
                fontSize: 12,
                border: '1px solid #FCA5A5',
              }}>
                {err}
              </div>
            )}
          </div>

          {/* Input */}
          <div style={{
            borderTop: `1px solid ${theme.border}`,
            padding: 12,
            display: 'flex',
            gap: 8,
          }}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Pregunta lo que necesites…"
              rows={1}
              style={{
                flex: 1,
                padding: '10px 12px',
                border: `1px solid ${theme.border}`,
                fontSize: 13,
                fontFamily: theme.font.sans,
                outline: 'none',
                resize: 'none',
                background: theme.bg,
                color: theme.text,
                minHeight: 40,
                maxHeight: 120,
              }}
            />
            <button
              onClick={send}
              disabled={loading || !input.trim()}
              style={{
                background: theme.accent,
                color: theme.brand,
                border: 'none',
                padding: '0 16px',
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                cursor: (loading || !input.trim()) ? 'not-allowed' : 'pointer',
                opacity: (loading || !input.trim()) ? 0.6 : 1,
                fontFamily: theme.font.sans,
              }}
            >
              {loading ? '…' : 'Enviar'}
            </button>
          </div>
          <div style={{
            padding: '0 12px 10px',
            textAlign: 'right',
          }}>
            <button
              onClick={clearChat}
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.textMuted,
                fontSize: 10,
                fontFamily: theme.font.sans,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Reiniciar conversación
            </button>
          </div>
        </aside>
      )}
    </>
  );
}
